import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle2, X, MessageCircle, Mail, Share2, Copy, Check, ExternalLink, Sparkles } from 'lucide-react';

/**
 * Fired the *one* moment a doctor's profile transitions from
 * "incomplete (noindex, hidden from directory)" → "live on Google".
 *
 * Behaviour:
 *  • Confetti burst on mount.
 *  • Sticky modal celebrating + offering one-tap share.
 *  • Server-side flag `published_celebrated_at` is set so this never fires
 *    a second time even if the doctor briefly drops below the threshold and
 *    returns above it later (don't make the unlock feel cheap).
 */
export default function ProfilePublishedCelebration({ slug, doctorName, onClose }) {
  const fired = useRef(false);
  const [copied, setCopied] = useState(false);
  const profileUrl = `https://docsite.care/dr/${slug}`;
  const shareMsg =
    `I just went live on DocSite — my own clinical website at ${profileUrl}. ` +
    `Patients can now find me on Google by my name + city. ` +
    `If you're a doctor too, you can set up yours at https://docsite.care.`;

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // Two-burst confetti from each side — feels more celebratory than a single shot.
    const fire = (origin) => confetti({
      particleCount: 80, spread: 70, origin, scalar: 1.05,
      colors: ['#0ea5e9', '#10b981', '#a855f7', '#f59e0b', '#ec4899'],
    });
    fire({ x: 0.2, y: 0.6 });
    setTimeout(() => fire({ x: 0.8, y: 0.6 }), 180);
    setTimeout(() => fire({ x: 0.5, y: 0.4 }), 360);
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Dr. ${doctorName} — on DocSite`, text: shareMsg, url: profileUrl }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("I just went live on DocSite")}&body=${encodeURIComponent(shareMsg)}`;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      data-testid="profile-published-celebration" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 z-10"
          data-testid="celebration-close">
          <X size={20} />
        </button>
        {/* Hero */}
        <div className="bg-gradient-to-br from-emerald-500 via-sky-500 to-violet-600 text-white p-8 text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-white/20 backdrop-blur items-center justify-center mb-3">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            You're live on Google
          </h2>
          <p className="text-emerald-50 text-sm mt-2 leading-relaxed">
            Dr. {doctorName}, your DocSite profile is now indexed and listed in your specialty + city directory. Patients searching for you will find you.
          </p>
        </div>

        {/* Share */}
        <div className="p-6 space-y-4">
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <Sparkles size={11} /> Tell the world
            </p>
            <p className="text-xs text-slate-600 leading-relaxed">
              The fastest way to win your first patients is to share your DocSite link with your contacts now — while the moment is fresh.
            </p>
          </div>

          {/* Profile URL pill */}
          <a href={profileUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 group"
            data-testid="celebration-profile-link">
            <span className="flex-1 text-xs font-mono text-slate-700 truncate">{profileUrl}</span>
            <ExternalLink size={14} className="text-slate-400 group-hover:text-slate-700" />
          </a>

          {/* Share buttons */}
          <div className="grid grid-cols-2 gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="celebration-share-whatsapp"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold border border-emerald-200">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={mailHref} data-testid="celebration-share-email"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 text-sm font-semibold border border-sky-200">
              <Mail size={15} /> Email
            </a>
            <button onClick={nativeShare} data-testid="celebration-share-native"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold border border-slate-200">
              <Share2 size={15} /> Share
            </button>
            <button onClick={copyLink} data-testid="celebration-share-copy"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold border border-slate-200">
              {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
            </button>
          </div>

          <button onClick={onClose}
            className="w-full text-xs text-slate-500 hover:text-slate-700 py-2 mt-1"
            data-testid="celebration-dismiss">
            I'll share later, thanks
          </button>
        </div>
      </div>
    </div>
  );
}
